import React from 'react';

export const Button = ({
  children,
  variant = 'secondary', // 'primary', 'secondary', 'ghost', 'danger', 'recessed'
  size = 'md', // 'sm', 'md', 'lg'
  icon,
  iconRight,
  onClick,
  disabled = false,
  className = '',
  type = 'button',
  fullWidth = false,
  ...props
}) => {
  const sizeStyles = {
    sm: { padding: '6px 12px', fontSize: '12px', borderRadius: 'var(--radius-sm)' },
    md: { padding: '9px 18px', fontSize: '14px', borderRadius: 'var(--radius-md)' },
    lg: { padding: '12px 24px', fontSize: '16px', borderRadius: 'var(--radius-lg)' }
  }[size];

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--accent-container)',
          color: '#ffffff',
          boxShadow: 'var(--shadow-raised-sm)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          fontWeight: '600'
        };
      case 'recessed':
        return {
          backgroundColor: 'var(--surface-recessed)',
          color: 'var(--accent-teal)',
          boxShadow: 'var(--shadow-recessed-sm)',
          border: '1px solid var(--border-subtle)',
          fontWeight: '600'
        };
      case 'danger':
        return {
          backgroundColor: 'var(--surface-card)',
          color: 'var(--color-danger)',
          boxShadow: 'var(--shadow-raised-sm)',
          border: '1px solid rgba(214, 92, 103, 0.3)',
          fontWeight: '550'
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: 'var(--text-secondary)',
          boxShadow: 'none',
          border: '1px solid transparent',
          fontWeight: '500'
        };
      case 'secondary':
      default:
        return {
          backgroundColor: 'var(--surface-card)',
          color: 'var(--text-primary)',
          boxShadow: 'var(--shadow-raised-sm)',
          border: '1px solid var(--border-subtle)',
          fontWeight: '500'
        };
    }
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        width: fullWidth ? '100%' : 'auto',
        userSelect: 'none',
        fontFamily: 'var(--font-body)',
        ...sizeStyles,
        ...getVariantStyles()
      }}
      className={`interactive-btn ${className}`}
      {...props}
    >
      {icon && <span style={{ display: 'inline-flex' }}>{icon}</span>}
      {children}
      {iconRight && <span style={{ display: 'inline-flex' }}>{iconRight}</span>}
    </button>
  );
};

export const IconButton = ({
  icon,
  onClick,
  variant = 'secondary',
  size = 'md',
  title,
  disabled = false,
  className = '',
  ...props
}) => {
  const dimensions = {
    sm: { width: '30px', height: '30px', padding: '5px' },
    md: { width: '38px', height: '38px', padding: '8px' },
    lg: { width: '46px', height: '46px', padding: '10px' }
  }[size];

  return (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={className}
      style={{
        ...dimensions,
        borderRadius: 'var(--radius-md)',
        padding: 0,
        minWidth: dimensions.width
      }}
      {...props}
    >
      {icon}
    </Button>
  );
};
